import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player/file';
import cm from 'classnames';

import { EFFECT_VALUE_CSS_OFFSET } from 'constants/params';
import { formatTimeWithMilliseconds } from './_player.helper';
import styles from './_enhanced-video-player.module.scss';

interface Props {
  playerRef: React.RefObject<ReactPlayer>;
  videoContainerRef: React.RefObject<HTMLDivElement>;
  cssFilterRef: React.RefObject<HTMLDivElement>;
  cssFilter: string;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isMuted: boolean;
  currentVolume: number;
  onPlayPause: () => void;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
  onSeek: (time: number) => void;
  rotateData: { rotate: number; flip: boolean };
  brightness: number;
  contrast: number;
  isSeeking: boolean;
  setIsSeeking: (seeking: boolean) => void;
  pendingSeek: number | null;
  setPendingSeek: (seek: number | null) => void;
}

const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export const CustomControls = ({
  playerRef,
  videoContainerRef,
  cssFilterRef,
  cssFilter,
  currentTime,
  duration,
  isPlaying,
  isMuted,
  currentVolume,
  onPlayPause,
  onVolumeChange,
  onMuteToggle,
  onSeek,
  rotateData,
  brightness,
  contrast,
  isSeeking,
  setIsSeeking,
  pendingSeek,
  setPendingSeek,
}: Props) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [seekTime, setSeekTime] = useState(0);
  const [customPiPWindow, setCustomPiPWindow] = useState<HTMLDivElement | null>(null);

  const sliderValue = pendingSeek !== null ? pendingSeek : currentTime;

  // Memoize expensive calculations
  const transformString = useMemo(
    () => `rotate(${rotateData.rotate * 90}deg)${rotateData.flip ? 'scaleY(-1)' : ''}`,
    [rotateData.rotate, rotateData.flip]
  );

  const filterString = useMemo(
    () => `brightness(${brightness + EFFECT_VALUE_CSS_OFFSET}%) contrast(${contrast + EFFECT_VALUE_CSS_OFFSET}%)`,
    [brightness, contrast]
  );

  // Memoize time formatting to avoid recalculation on every frame
  const formattedCurrentTime = useMemo(
    () => formatTimeWithMilliseconds(currentTime),
    [Math.floor(currentTime * 1000)] // Only update every second
  );

  const formattedDuration = useMemo(() => formatTimeWithMilliseconds(duration), [Math.floor(duration * 1000)]);

  // Memoize volume fill percentage
  const volumeFillPercentage = useMemo(
    () => `${(isMuted ? 0 : currentVolume) * 100}%`,
    [isMuted, Math.round(currentVolume * 100)]
  );

  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const time = parseFloat(event.target.value);
      setSeekTime(time);
      setIsSeeking(true);
      setPendingSeek(time);
      // Live scrubbing: call seekTo immediately
      playerRef.current?.seekTo(time, 'seconds');
      onSeek(time);
    },
    [currentTime, isSeeking, seekTime, setIsSeeking, playerRef, onSeek]
  );

  const handleSliderMouseUp = useCallback(() => {
    setIsSeeking(false);
    // Do not clear pendingSeek here; wait for player to catch up
  }, [seekTime, isSeeking]);

  const handleFullscreenToggle = useCallback(() => {
    if (!document.fullscreenElement) {
      // Enter fullscreen - target the video wrapper that contains the transformed content
      const videoWrapper = videoContainerRef.current;
      if (videoWrapper) {
        videoWrapper
          .requestFullscreen()
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((err: Error) => {
            console.error('Error entering fullscreen:', err);
          });
      }
    } else {
      // Exit fullscreen
      document
        .exitFullscreen()
        .then(() => {
          setIsFullscreen(false);
        })
        .catch((err: Error) => {
          console.error('Error exiting fullscreen:', err);
        });
    }
  }, [videoContainerRef]);

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      setPlaybackRate(rate);

      // Apply to video element
      const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
      if (videoElement) {
        videoElement.playbackRate = rate;
      }
    },
    [playerRef]
  );

  const handleOptionsToggle = useCallback(() => {
    setShowOptions(!showOptions);
  }, [showOptions]);

  const handlePiPToggle = useCallback(async () => {
    const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    if (!videoElement) return;

    try {
      if (isPiPActive) {
        // Exit custom PiP
        if (customPiPWindow) {
          // Find the video element in the PiP window and restore it
          const cssFilterElement = cssFilterRef.current;
          if (cssFilterElement) {
            cssFilterElement.appendChild(videoElement);
          }
          customPiPWindow.remove();
          setCustomPiPWindow(null);
        }
        setIsPiPActive(false);
      } else {
        // Create custom PiP window with transformations
        console.log('Creating custom PiP with transformations:', {
          transformString,
          filterString,
          rotateData,
          brightness,
          contrast,
        });

        // Create floating window
        const pipWindow = document.createElement('div');
        pipWindow.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          width: 320px;
          height: 180px;
          background: black;
          border: 2px solid #333;
          border-radius: 8px;
          z-index: 9999;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          cursor: move;
          resize: both;
          display: flex;
          align-items: center;
          justify-content: center;
        `;

        // Alternative approach: use original video in transformed container
        const videoContainer = document.createElement('div');
        videoContainer.style.cssText = `
          width: 100%;
          height: 100%;
          transform: ${transformString};
          filter: ${cssFilter};
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
        `;

        // Move the original video to the PiP container temporarily
        const originalParent = videoElement.parentElement;
        videoContainer.appendChild(videoElement);
        pipWindow.appendChild(videoContainer);

        // Store reference to restore original position later
        pipWindow.dataset.originalParent = originalParent?.tagName || '';

        document.body.appendChild(pipWindow);

        // Make window draggable
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let startLeft = 0;
        let startTop = 0;

        pipWindow.addEventListener('mousedown', (e) => {
          if (e.target === pipWindow) {
            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            const rect = pipWindow.getBoundingClientRect();
            startLeft = rect.left;
            startTop = rect.top;
            pipWindow.style.cursor = 'grabbing';
          }
        });

        document.addEventListener('mousemove', (e) => {
          if (isDragging) {
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;
            pipWindow.style.left = `${startLeft + deltaX}px`;
            pipWindow.style.top = `${startTop + deltaY}px`;
          }
        });

        document.addEventListener('mouseup', () => {
          if (isDragging) {
            isDragging = false;
            pipWindow.style.cursor = 'move';
          }
        });

        // Add close button
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '×';
        closeButton.style.cssText = `
          position: absolute;
          top: 5px;
          right: 5px;
          width: 20px;
          height: 20px;
          background: rgba(0,0,0,0.7);
          color: white;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          font-size: 14px;
          z-index: 1;
        `;
        closeButton.onclick = () => {
          // Restore video to original position
          if (originalParent) {
            originalParent.appendChild(videoElement);
          }
          pipWindow.remove();
          setCustomPiPWindow(null);
          setIsPiPActive(false);
        };
        pipWindow.appendChild(closeButton);

        setCustomPiPWindow(pipWindow);
        setIsPiPActive(true);
      }
    } catch (err) {
      console.error('Error toggling Picture-in-Picture:', err);
    }
  }, [playerRef, cssFilter, rotateData, brightness, contrast, isPiPActive, customPiPWindow, isPlaying]);

  const handleDownload = useCallback(() => {
    const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    if (!videoElement) {
      console.error('Video element not found');
      return;
    }

    // Try to get the video source
    let videoUrl = videoElement.src;

    // If no src, try to get from currentSrc (for multiple sources)
    if (!videoUrl) {
      videoUrl = videoElement.currentSrc;
    }

    // If still no URL, try to get from ReactPlayer's config
    if (!videoUrl && playerRef.current?.props?.url) {
      const playerUrl = playerRef.current.props.url;
      // Handle different URL types that ReactPlayer supports
      if (typeof playerUrl === 'string') {
        videoUrl = playerUrl;
      } else if (Array.isArray(playerUrl)) {
        // If it's an array, take the first string URL
        const firstUrl = playerUrl.find((url) => typeof url === 'string');
        if (firstUrl && typeof firstUrl === 'string') {
          videoUrl = firstUrl;
        }
      }
    }

    if (!videoUrl) {
      console.error('No video URL found for download');
      alert('Download not available for this video');
      return;
    }

    // Extract filename from URL or use default
    let filename = 'video.mp4';
    try {
      const url = new URL(videoUrl);
      const pathname = url.pathname;
      const urlFilename = pathname.split('/').pop();
      if (urlFilename && urlFilename.includes('.')) {
        filename = urlFilename;
      }
    } catch (e) {
      console.warn('Could not parse video URL for filename extraction');
    }

    // Check if it's a blob URL or data URL (these can't be downloaded directly)
    if (videoUrl.startsWith('blob:') || videoUrl.startsWith('data:')) {
      console.error('Cannot download blob or data URLs directly');
      alert('Download not available for this video type');
      return;
    }

    // For cross-origin videos, we might need to handle differently
    if (videoUrl.startsWith('http') && !videoUrl.startsWith(window.location.origin)) {
      console.warn('Cross-origin video download may be restricted by CORS');
    }

    try {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = filename;
      link.target = '_blank'; // Open in new tab as fallback

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error initiating download:', error);

      // Fallback: open in new tab
      try {
        window.open(videoUrl, '_blank');
        console.warn('Opened video in new tab as fallback');
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        alert('Unable to download video. Please try right-clicking the video and selecting "Save video as..."');
      }
    }
  }, [playerRef]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Sync playback rate when video element is ready
  useEffect(() => {
    const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    if (videoElement && playbackRate !== 1) {
      videoElement.playbackRate = playbackRate;
    }
  }, [playbackRate, playerRef.current]);

  // Close options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is inside the options container or dropdown using data attributes
      const optionsContainer = target.closest('[data-options-container]');
      const optionsDropdown = target.closest('[data-options-dropdown]');

      // Only close if we're not clicking inside the options container or dropdown
      if (showOptions && !optionsContainer && !optionsDropdown) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  // Update PiP window transformations when they change - Optimized
  useEffect(() => {
    if (isPiPActive && customPiPWindow) {
      // Find the video container in the PiP window and update its styles
      const videoContainer = customPiPWindow.querySelector('div') as HTMLElement;
      if (videoContainer) {
        videoContainer.style.transform = transformString;
        videoContainer.style.filter = filterString;
      }
    }
  }, [isPiPActive, customPiPWindow, transformString, filterString]);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining PiP window
      if (customPiPWindow) {
        customPiPWindow.remove();
      }
    };
  }, [customPiPWindow]);

  return (
    <div className={styles.customControls}>
      {/* Top row - Play/Pause, Time Display, Volume Controls, Fullscreen, More Options */}
      <div className={styles.controlsRow}>
        {/* Play/Pause Button */}
        <button
          className={cm(styles.controlButton, styles.playPauseButton)}
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>

        {/* Time Display */}
        <div className={styles.timeDisplay}>
          <span className={styles.currentTime}>{formattedCurrentTime}</span>
          <span className={styles.duration}>{formattedDuration}</span>
        </div>

        {/* Volume Controls */}
        <div className={styles.volumeContainer}>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={isMuted ? 0 : currentVolume}
            onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
            className={styles.volumeSlider}
            aria-label="Volume"
            style={
              {
                '--volume-fill': volumeFillPercentage,
              } as React.CSSProperties
            }
            onFocus={(e) => e.target.blur()}
          />
          <button
            className={cm(styles.controlButton, styles.volumeButton)}
            onClick={onMuteToggle}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || currentVolume === 0 ? '🔇' : currentVolume < 0.5 ? '🔉' : '🔊'}
          </button>
        </div>

        {/* Fullscreen Button */}
        <button
          className={cm(styles.controlButton, styles.fullscreenButton)}
          onClick={handleFullscreenToggle}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? '⏹️' : '⛶'}
        </button>

        {/* More Options Button */}
        <div className={styles.optionsContainer} data-options-container="true">
          <button
            className={cm(styles.controlButton, styles.optionsButton)}
            onClick={handleOptionsToggle}
            aria-label="More options"
          >
            ⋮
          </button>

          {/* Options Dropdown */}
          {showOptions && (
            <div
              className={styles.optionsDropdown}
              data-options-dropdown="true"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Playback Speed Section */}
              <div className={styles.optionsSection}>
                <div className={styles.optionsTitle}>Playback Speed</div>
                <div className={styles.playbackRates}>
                  {PLAYBACK_RATES.map((rate) => (
                    <button
                      key={rate}
                      className={cm(styles.rateButton, {
                        [styles.rateButtonActive]: playbackRate === rate,
                      })}
                      onClick={() => handlePlaybackRateChange(rate)}
                    >
                      {rate === 1 ? 'Normal' : `${rate}x`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className={styles.optionsDivider}></div>

              {/* Video Options Section */}
              <div className={styles.optionsSection}>
                <div className={styles.optionsTitle}>Video Options</div>
                <div className={styles.videoOptions}>
                  <button
                    className={cm(styles.optionButton, {
                      [styles.optionButtonActive]: isPiPActive,
                    })}
                    onClick={handlePiPToggle}
                    disabled={!document.pictureInPictureEnabled}
                  >
                    <span className={styles.optionIcon}>📺</span>
                    {isPiPActive ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'}
                  </button>

                  <button className={styles.optionButton} onClick={handleDownload}>
                    <span className={styles.optionIcon}>⬇️</span>
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom row - Progress Slider (full width) */}
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${(currentTime / duration) * 100}%` }} />
        </div>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.001} // 1ms precision
          value={sliderValue}
          onChange={handleSliderChange}
          onMouseUp={handleSliderMouseUp}
          onTouchEnd={handleSliderMouseUp}
          onFocus={(e) => e.target.blur()}
          className={cm(styles.progressSlider, { [styles.seeking]: isSeeking })}
          aria-label="Video progress"
        />
      </div>
    </div>
  );
};
